import {
  NEW_SYNC_HTTP_BULK_PAGE_BYTE_CAP,
  NEW_SYNC_HTTP_BULK_PAGE_ROW_CAP,
} from "@cord/shared"
import {
  fetchThreadSyncBaselinePage as fetchThreadSyncBaselinePageGeneric,
  fetchThreadSyncBootstrapArtifact as fetchThreadSyncBootstrapArtifactGeneric,
  fetchThreadSyncBootstrapArtifactBlob as fetchThreadSyncBootstrapArtifactBlobGeneric,
  fetchThreadSyncBootstrapEligibility as fetchThreadSyncBootstrapEligibilityGeneric,
  fetchThreadSyncCatchupPage as fetchThreadSyncCatchupPageGeneric,
  type ThreadSyncBootstrapArtifactResponse,
  type ThreadSyncBootstrapEligibilityResponse,
  type ThreadSyncRuntimeCheckpointBlob,
  type ThreadSyncHttpBaselinePageResponse,
  type ThreadSyncHttpCatchupResponse,
} from "@/lib/thread-sync-http-catchup-client"

export type {
  ThreadSyncHttpBaselinePageResponse,
  ThreadSyncBootstrapArtifactResponse,
  ThreadSyncBootstrapEligibilityResponse,
  ThreadSyncHttpCatchupResponse,
  ThreadSyncRuntimeCheckpointBlob,
} from "@/lib/thread-sync-http-catchup-client"

export type ThreadSyncHttpPrefillMode =
  | "validated_prefix"
  | "runtime_checkpoint_probe"
  | "empty_prefix_bootstrap"
  | "empty_prefix_fallback"
  | "terminal_cursor_catchup"

/**
 * Reuse the existing HTTP bulk-history page geometry so active-thread prefill
 * benefits from the same throughput improvements as background warming instead
 * of inventing a third transport budget.
 */
export const ACTIVE_THREAD_HTTP_PREFILL_PAGE_ROW_CAP = NEW_SYNC_HTTP_BULK_PAGE_ROW_CAP

/**
 * Match the shared bulk-history byte budget because this path is also a bounded
 * read-only catchup lane. If one-megabyte pages prove too bursty on active
 * activation, tune the shared transport story deliberately rather than letting
 * the active path drift into bespoke sizing.
 */
export const ACTIVE_THREAD_HTTP_PREFILL_PAGE_BYTE_CAP = NEW_SYNC_HTTP_BULK_PAGE_BYTE_CAP

/**
 * Give the HTTP lane roughly one second to win a cold-switch head start before
 * we fall back to websocket history. This keeps the added pre-hello wait small
 * enough that live/control attachment still starts promptly on slower networks.
 */
export const ACTIVE_THREAD_HTTP_PREFILL_MAX_WAIT_MS = 1_000

/**
 * Fetch at most three bulk pages during the pre-hello window so a cold switch
 * can make meaningful cursor progress without turning one activation into an
 * open-ended hidden replay job.
 */
export const ACTIVE_THREAD_HTTP_PREFILL_MAX_PAGES = 3

/**
 * Mirror the page cap with a total byte cap so a few unusually large pages do
 * not monopolize one thread switch. This is intentionally derived from the
 * shared page budget to keep the aggregate read cost obvious.
 */
export const ACTIVE_THREAD_HTTP_PREFILL_MAX_TOTAL_BYTES =
  ACTIVE_THREAD_HTTP_PREFILL_PAGE_BYTE_CAP * ACTIVE_THREAD_HTTP_PREFILL_MAX_PAGES

/**
 * Give brand-new-browser empty-prefix loads longer than the arbitrary-switch
 * fast path so HTTP can advance materially farther before websocket history
 * takes over. Two and a half seconds is deliberately still bounded: it is long
 * enough to buy several extra bulk round-trips on typical broadband, but short
 * enough that a slow or stalled request still yields to live websocket resume
 * without feeling like the page is hung forever.
 */
export const EMPTY_PREFIX_HTTP_BOOTSTRAP_PREFILL_MAX_WAIT_MS = 2_500

/**
 * Runtime checkpoints replace replaying tens of thousands of provider events, so
 * once the server advertises a checkpoint we should give the blob fetch,
 * validation, JSON parse, runtime hydrate, and durable-import path a materially
 * larger budget than ordinary HTTP prefill. Fifteen seconds is still bounded for
 * broken networks; if slow mobile restores routinely exceed this, tune from
 * checkpoint phase telemetry rather than making restore unbounded.
 */
export const RUNTIME_CHECKPOINT_BOOTSTRAP_MAX_WAIT_MS = 15_000

/**
 * Allow significantly more than the three-page arbitrary-switch head start when
 * the browser has no usable local prefix at all. Eight pages is large enough to
 * cover a meaningful recent slice of long sessions while still keeping the
 * hidden pre-hello bootstrap bounded. If this still feels too shallow on real
 * huge threads, raise it with measurements instead of removing the cap.
 */
export const EMPTY_PREFIX_HTTP_BOOTSTRAP_PREFILL_MAX_PAGES = 8

/**
 * Mirror the empty-prefix page budget with a byte ceiling so the deeper cold
 * bootstrap path cannot silently read an unbounded amount of transcript before
 * websocket handoff. The page geometry stays shared; only the total budget is
 * larger than the arbitrary-switch path.
 */
export const EMPTY_PREFIX_HTTP_BOOTSTRAP_PREFILL_MAX_TOTAL_BYTES =
  ACTIVE_THREAD_HTTP_PREFILL_PAGE_BYTE_CAP * EMPTY_PREFIX_HTTP_BOOTSTRAP_PREFILL_MAX_PAGES

export async function fetchThreadSyncCatchupPage(args: {
  sessionId: string
  after: number
  signal: AbortSignal
}): Promise<ThreadSyncHttpCatchupResponse> {
  return await fetchThreadSyncCatchupPageGeneric({
    ...args,
    maxRows: ACTIVE_THREAD_HTTP_PREFILL_PAGE_ROW_CAP,
    maxBytes: ACTIVE_THREAD_HTTP_PREFILL_PAGE_BYTE_CAP,
    purpose: "foreground_http_prefill",
  })
}

export async function fetchThreadSyncBaselinePage(args: {
  sessionId: string
  afterBaselineSeq: number
  signal: AbortSignal
}): Promise<ThreadSyncHttpBaselinePageResponse> {
  return await fetchThreadSyncBaselinePageGeneric({
    ...args,
    maxRows: ACTIVE_THREAD_HTTP_PREFILL_PAGE_ROW_CAP,
    maxBytes: ACTIVE_THREAD_HTTP_PREFILL_PAGE_BYTE_CAP,
    purpose: "foreground_http_prefill",
  })
}

export async function fetchThreadSyncBootstrapEligibility(args: {
  sessionId: string
  signal: AbortSignal
}): Promise<ThreadSyncBootstrapEligibilityResponse> {
  return await fetchThreadSyncBootstrapEligibilityGeneric(args)
}

export async function fetchThreadSyncBootstrapArtifact(args: {
  sessionId: string
  conversationId?: string | null
  signal: AbortSignal
}): Promise<ThreadSyncBootstrapArtifactResponse> {
  return await fetchThreadSyncBootstrapArtifactGeneric(args)
}

export async function fetchThreadSyncBootstrapArtifactBlob(args: {
  blobFetchPath: string
  signal: AbortSignal
}): Promise<ArrayBuffer> {
  return await fetchThreadSyncBootstrapArtifactBlobGeneric(args)
}
